/* eslint-disable testing-library/no-debugging-utils */
// in general, want to avoid user-agent sniffing to determine if we are mobile or not
// This technique uses a few browser checks to infer if the device is mobile:
//
// Check 1: maxTouchPoints
//
//       if the browser has maxTouchpoints AND maxTouchpoint > 0, then this device has a touchscreen with multitouch capability.
//       Most likely it is a phone
//       This has good support, 94.9% of all users ; https://caniuse.com/mdn-api_navigator_maxtouchpoints
//
//
// Check 2: Media Query for pointer:coarse
//
//      if the browser reports that the input mechanism of the device includes a pointing device of limited accuracy, then
//      most likely it is a phone.
//      This has pretty good browser support, 96.2% of all users : https://caniuse.com/css-media-interaction
//
// Check 3: Is windows API "orientation" supported in the browser?
//
//      The "orientation" feature is deprecated, BUT it is only present in mobile devices, and not on desktop
//      So including it as an additional signal anyway
//      Ony supports 55.2% of all browsers, but they are all mobile: https://caniuse.com/mdn-api_window_orientation
//
//
// Check 4: Does the user agent string match known mobile user agents?
//
//      This is a last resort
//
// Additional resources:
// - https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_device_detection
// - https://stackoverflow.com/questions/72502079/how-can-i-check-if-the-device-which-is-using-my-website-is-a-mobile-user-or-no
//

// TODO: Consider refactoring

import { Logger } from '@egw/logger'

const log = new Logger('Auth | Mobile Utils')

const UNKNOWN = 'UNKNOWN'

type TBooleanOrUnknown = boolean | 'UNKNOWN'

export interface TDeviceChecks {
  hasTouchScreen: TBooleanOrUnknown
  hasPointerCoarse: TBooleanOrUnknown
  hasOrientation: TBooleanOrUnknown
  hasMobileUserAgent: TBooleanOrUnknown
}

export interface TIsUserUsingMobileDevice {
  isDeviceMobile: boolean
  deviceChecks: TDeviceChecks
}

const checkNotUnknown = (arg: any) => arg !== UNKNOWN && Boolean(arg)

export const isUserUsingMobileDevice = (): TIsUserUsingMobileDevice => {
  // Check 1: Does device have multipoint touchscreen?
  const deviceChecks: TDeviceChecks = {
    hasTouchScreen: false,
    hasPointerCoarse: false,
    hasOrientation: false,
    hasMobileUserAgent: false,
  }

  if (typeof window !== 'undefined' && window.navigator) {
    if (window.navigator.maxTouchPoints > 0) {
      deviceChecks.hasTouchScreen = true
    }
    // console log results
  } else {
    deviceChecks.hasTouchScreen = UNKNOWN
  }
  log.debug('| isUserUsingMobileDevice ', `Device Check 1: Multipoint Touchscreen -> ${deviceChecks.hasTouchScreen}`)

  // Check 2: Does device have pointer coarse?
  if (window.matchMedia) {
    const mQ = window.matchMedia && window.matchMedia('(pointer:coarse)')
    if (mQ && mQ.media == '(pointer:coarse)') {
      deviceChecks.hasPointerCoarse = Boolean(mQ.matches)
    }
  } else {
    deviceChecks.hasPointerCoarse = UNKNOWN
  }
  log.debug('| isUserUsingMobileDevice ', `Device Check 2: Coarse Pointer -> ${deviceChecks.hasPointerCoarse}`)

  // Check 3: Does the browser support screen.orientation?
  if (window.orientation) {
    deviceChecks.hasOrientation = true /* deprecated, but good fallback */
  }
  log.debug('| isUserUsingMobileDevice ', `Device Check 3: Has Orientation -> ${deviceChecks.hasOrientation}`)

  // Check 4: Does the user agent match a known mobile device?
  if (window.navigator.userAgent) {
    deviceChecks.hasMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      window.navigator.userAgent,
    )
  } else {
    deviceChecks.hasMobileUserAgent = UNKNOWN
  }
  log.debug('| isUserUsingMobileDevice ', `Device Check 4: Mobile User Agent -> ${deviceChecks.hasMobileUserAgent}`)

  let isDeviceMobile = false
  if (checkNotUnknown(deviceChecks.hasTouchScreen) && checkNotUnknown(deviceChecks.hasPointerCoarse)) {
    // if device has a multitouch screen & a coarse pointer, then it is mobile
    isDeviceMobile = true
  } else if (checkNotUnknown(deviceChecks.hasOrientation)) {
    // if those checks failed, but it has orientation, then it is mobile
    isDeviceMobile = true
  } else if (checkNotUnknown(deviceChecks.hasMobileUserAgent)) {
    // if all else fails, as a last resort check user agent string
    isDeviceMobile = true
  }

  return {
    isDeviceMobile,
    deviceChecks,
  }
}
